import React from 'react';
import { ScanStatusBadge } from './ScanStatusBadge';
import { TrashIcon } from './StatusIcons';
import { UiMessages } from '../uiMessages';

export function CaseFileCard({
  item,
  isReadOnly,
  canOpenPlayer,
  onDelete,
  onDeleteTranscript,
  onOpenPlayer,
  onBrowseAddTranscript,
  onRemoteTranscriptFileChange,
  setRemoteTranscriptInputRef,
  transcriptAccept,
  isAddingTranscript,
  getEffectiveTranscriptRecord,
  resolveCaseFileMediaTone,
  resolveCaseFileTranscriptTone,
  isVirusDetectedMediaRecord,
  isTranscriptVirusDetected,
  isVirusStatus,
  isLocalUploadFailed,
  getFirstRecordValue,
  getRecordMediaId,
  getRecordTranscriptFileId
}) {
  const mediaRecord = item?.mediaRecord ?? {};
  const transcriptRecord = getEffectiveTranscriptRecord(mediaRecord, item?.transcriptRecord ?? null);
  const localUploadItem = item?.localUploadItem ?? null;
  const mediaTone = resolveCaseFileMediaTone(mediaRecord, localUploadItem);
  const transcriptTone = resolveCaseFileTranscriptTone(transcriptRecord, localUploadItem, mediaRecord);
  const canDelete = !isReadOnly;
  const hasVirus = isVirusDetectedMediaRecord(mediaRecord)
    || isTranscriptVirusDetected(mediaRecord, transcriptRecord)
    || isVirusStatus(localUploadItem?.scanStatus)
    || isVirusStatus(localUploadItem?.transcriptScanStatus);
  const hasRejectedAppearance = hasVirus
    || mediaTone === 'failed'
    || transcriptTone === 'failed'
    || isLocalUploadFailed(localUploadItem);
  const mediaDeleteButtonStyle = mediaTone === 'quarantine' || mediaTone === 'failed'
    ? { color: '#b91c1c' }
    : { color: '#64748b' };
  const transcriptDeleteButtonStyle = transcriptTone === 'quarantine' || transcriptTone === 'failed'
    ? { color: '#b91c1c' }
    : { color: '#64748b' };
  const mediaDeleteButtonClassName = mediaTone === 'quarantine' || mediaTone === 'failed'
    ? 'icon-action-button file-delete-button case-file-delete-button'
    : 'icon-action-button';
  const transcriptDeleteButtonClassName = transcriptTone === 'quarantine' || transcriptTone === 'failed'
    ? 'icon-action-button file-delete-button case-file-transcript-delete-button'
    : 'icon-action-button';
  const mediaName = getFirstRecordValue(mediaRecord, ['storedFileName', 'StoredFileName', 'fileName', 'FileName', 'originalFileName', 'OriginalFileName']) || getRecordMediaId(mediaRecord) || 'Media file';
  const transcriptName = getFirstRecordValue(transcriptRecord, ['storedFileName', 'StoredFileName', 'fileName', 'FileName', 'originalFileName', 'OriginalFileName']) || getRecordTranscriptFileId(transcriptRecord) || 'Transcript';
  const canOpenMediaPlayer = canOpenPlayer && typeof onOpenPlayer === 'function';
  const currentDescription = getFirstRecordValue(mediaRecord, ['description', 'Description']) || '';
  const mediaId = getRecordMediaId(mediaRecord) || '';
  const canAddTranscript = !isReadOnly && !transcriptRecord && Boolean(mediaId) && typeof onBrowseAddTranscript === 'function';

  return (
    <div className={`case-file-card ${canDelete ? 'case-file-card-editable' : 'case-file-card-locked'} ${hasRejectedAppearance ? 'case-file-card-virus' : ''}`}>
      <div className="case-file-row-header case-file-row-header-with-desc">
        <div className="file-heading-row case-file-heading-row case-file-heading-compact">
          <div className="file-heading-content case-file-heading-content">
            {canOpenMediaPlayer ? (
              <button
                type="button"
                className="case-file-title case-file-open-player-button"
                onClick={() => onOpenPlayer(item)}
                aria-label={`Open media player for ${mediaName}`}
              >
                {mediaName}
              </button>
            ) : (
              <div className="case-file-title" style={{ textDecoration: 'none', cursor: 'default' }}>{mediaName}</div>
            )}
            <ScanStatusBadge tone={mediaTone} />
          </div>
        </div>
        <div className="case-file-description-col">
          <input
            type="text"
            className="description-input case-file-description-input"
            placeholder="Description (optional)"
            value={currentDescription}
            readOnly
          />
        </div>
        {canDelete ? (
          <button
            className={mediaDeleteButtonClassName}
            type="button"
            style={mediaDeleteButtonStyle}
            onClick={() => onDelete?.(item)}
            aria-label={`Delete media file ${mediaName}`}
          >
            <TrashIcon />
          </button>
        ) : null}
      </div>

      {transcriptRecord ? (
        <div className="case-file-transcript-row case-file-transcript-row-with-desc">
          <div className="case-file-transcript-heading-compact">
            <div className="case-file-transcript-content">
              <div className="case-file-transcript-title">{transcriptName}</div>
              <ScanStatusBadge tone={transcriptTone} />
            </div>
          </div>
          <div className="case-file-description-col">
            <input
              type="text"
              className="description-input case-file-description-input"
              placeholder="Description (optional)"
              value={getFirstRecordValue(transcriptRecord, ['description', 'Description']) || ''}
              readOnly
            />
          </div>
          {canDelete ? (
            <button
              className={transcriptDeleteButtonClassName}
              type="button"
              style={transcriptDeleteButtonStyle}
              onClick={() => onDeleteTranscript?.(item)}
              aria-label={`Delete transcript file ${transcriptName}`}
            >
              <TrashIcon />
            </button>
          ) : null}
        </div>
      ) : (
        <div className="case-file-transcript-empty text-muted">
          <span>{UiMessages.transcriptNone}</span>
          {canAddTranscript ? (
            <>
              {' - '}
              <button
                type="button"
                className="case-file-add-transcript-link"
                onClick={() => onBrowseAddTranscript?.(item)}
                disabled={Boolean(isAddingTranscript)}
              >
                {isAddingTranscript ? `${UiMessages.transcriptAddAction}...` : UiMessages.transcriptAddAction}
              </button>
              <input
                ref={(element) => setRemoteTranscriptInputRef?.(mediaId, element)}
                type="file"
                className="d-none"
                accept={transcriptAccept || '.pdf,application/pdf'}
                onChange={(event) => onRemoteTranscriptFileChange?.(item, event)}
              />
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}
